import { ChangeEvent, useState } from 'react'

import { AssetType } from 'src/lib/assets/assetsTypes'
import upload, { readFile, UploadedData } from 'src/lib/assets/upload'
import { Entity } from 'src/types'

import { resizeImage } from '../../../lib/resizeImage'
import { toast, toastPromise } from '../Toaster'

export type UsePhotoUploadProps = {
  assetId?: number | ((record: Entity) => Promise<number>)
  assetType: AssetType
  saveBeforeUpload?(): Promise<Entity>
  failCleanup?(record: Entity): void
  width: number
  height: number
  onFileUploaded?: (uploadData: UploadedData, record?: Entity) => void
  skipCrop?: boolean
}

export default function usePhotoUpload({
  assetId = async (asset: { id: number }) => asset.id,
  assetType,
  saveBeforeUpload,
  failCleanup,
  width,
  height,
  onFileUploaded,
  skipCrop,
}: UsePhotoUploadProps) {
  const [toCrop, setToCrop] = useState<string>()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)

  const resizeAndUpload = async (type: AssetType, id: number, file: Blob) =>
    upload(type, id, await resizeImage(file, height, width))

  const onChangeFile = async (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      if (!file.type.includes('image')) {
        toast('error', 'Invalid file type')
        return
      }

      if (skipCrop) {
        setIsSaving(true)
        let entity: Entity
        try {
          let entityId: number
          if (typeof assetId === 'number') {
            entityId = assetId
          } else {
            if (saveBeforeUpload) {
              entity = await saveBeforeUpload()
            }
            entityId = await assetId(entity)
          }

          const uploadPromise = resizeAndUpload(assetType, entityId, file)
          toastPromise(
            uploadPromise,
            {
              loading: { title: 'Uploading...' },
              success: { title: 'Done!' },
              error: { title: 'Oops, uploading failed.' },
            },
            { id: 'ReviewUpload' }
          )
          const uploadedData = await uploadPromise
          onFileUploaded(uploadedData, entity)
        } catch (err: unknown) {
          console.error(err)
          e.target.value = ''
          if (failCleanup && entity) {
            failCleanup(entity)
          }
        }
        setIsSaving(false)
      } else {
        const imageDataUrl = (await readFile(file)) as string
        setIsOpen(true)
        setToCrop(imageDataUrl)
      }
    }
    e.target.value = ''
  }

  const onEditPhoto = async (canvas: HTMLCanvasElement) => {
    setIsSaving(true)
    try {
      const croppedImage: Blob = await new Promise((resolve) =>
        canvas.toBlob((blob: Blob) => resolve(blob))
      )
      let entity: Entity
      let entityId: number
      if (typeof assetId === 'number') {
        entityId = assetId
      } else {
        if (saveBeforeUpload) {
          entity = await saveBeforeUpload()
        }
        entityId = await assetId(entity)
      }
      const uploadedData = await resizeAndUpload(
        assetType,
        entityId,
        croppedImage
      )
      onFileUploaded(uploadedData, entity)
    } catch (e) {
      toast('error', 'Oops, uploading failed.')
    }
    setIsOpen(false)
    setIsSaving(false)
  }

  return { toCrop, isOpen, isSaving, onChangeFile, onEditPhoto, setIsOpen }
}
